import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Spinner,
  Badge,
  Button
} from "reactstrap";
import { apiClient } from "../../apiClient";
import { endpoints } from "../../configs";
import moment from "moment";

const sectionsList = [
  {
    id: "performance",
    label: "Partner Performance Ranking",
    icon: "💎",
    description: "Top performing partners calculated via credits vs engagement"
  },
  {
    id: "funnel",
    label: "Conversion Funnel Behavioral",
    icon: "🌋",
    description: "Session-to-conversion progression across the partner stack"
  },
  {
    id: "referral_revenue",
    label: "Revenue Attribution Pipeline",
    icon: "💰",
    description: "Real-time pipeline credit across all active referrals"
  },
  {
    id: "hourly_trends",
    label: "Hourly Usage Density",
    icon: "⚡",
    description: "Peak operational hours and concurrency spikes detection"
  },
  {
    id: "engagement",
    label: "Partner Scoring Matrix",
    icon: "🧬",
    description:
      "Proprietary engagement scoring based on multi-dimensional activity"
  },
  {
    id: "combined",
    label: "Combined Activity Linkage",
    icon: "🧶",
    description: "Cross-platform event correlation for attribution validation"
  },
  {
    id: "daily_trends",
    label: "Daily Growth Trajectory",
    icon: "📈",
    description: "Day-over-day growth patterns of credit consumption"
  },
  {
    id: "session",
    label: "Session Sequence Flow",
    icon: "🌊",
    description: "User journey flow sequences within the Snowflake environment"
  },
  {
    id: "executive",
    label: "Executive KPI Summary",
    icon: "👔",
    description: "High-level operational metrics for boardroom reporting"
  },
  {
    id: "transition",
    label: "State Transition Matrix",
    icon: "🔄",
    description: "Probability of state shifts between engagement tiers"
  }
];

const SnowFlakeInsightCard = ({
  section,
  partnerId,
  startDate,
  endDate,
  index,
  isActive
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const sectionRef = useRef(null);

  // Fetch data function
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${endpoints().snowFlakeAPI}/insights?type=${section.id}${
        partnerId ? `&partnerId=${partnerId}` : ""
      }`;

      if (startDate && endDate) {
        const start = moment(startDate)
          .startOf("day")
          .toISOString();
        const end = moment(endDate)
          .endOf("day")
          .toISOString();
        url += `&start=${start}&end=${end}`;
      }

      const res = await apiClient.get(url);
      setData((res.data && res.data.data) || []);
      setHasLoaded(true);
    } catch (err) {
      console.error(`Error fetching ${section.id}:`, err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Immediate load for section 0 on mount or filter change
  useEffect(() => {
    if (index === 0) {
      fetchData();
    } else {
      // Reset others
      setHasLoaded(false);
      setData([]);
      setError(null);
    }
  }, [startDate, endDate, partnerId, index]);

  // Observer logic for sections > 0
  useEffect(() => {
    if (index === 0 || hasLoaded || loading) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          fetchData();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [index, hasLoaded, loading, section.id]);

  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "-";

    if (
      key.includes("DATE") ||
      key.endsWith("_AT") ||
      key.includes("HOUR") ||
      key.includes("_TIME")
    ) {
      return (
        <div className="d-flex flex-column">
          <span
            className="font-weight-bold text-dark"
            style={{ fontSize: "0.85rem" }}
          >
            {moment(value).format("MMM DD, YYYY")}
          </span>
          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
            {moment(value).format("HH:mm")}
          </span>
        </div>
      );
    }
    if (key.includes("PCT") || key.includes("RATE")) {
      const val = parseFloat(value);
      const color = val > 75 ? "#2ec4b6" : val > 40 ? "#3a86ff" : "#f4a261";
      return (
        <div className="d-flex align-items-center">
          <div
            className="progress flex-grow-1 mr-3"
            style={{
              height: "8px",
              minWidth: "80px",
              borderRadius: "4px",
              background: "#f1f5f9"
            }}
          >
            <div
              className="progress-bar"
              style={{
                width: `${Math.min(val, 100)}%`,
                background: color,
                boxShadow: `0 2px 4px ${color}44`
              }}
            ></div>
          </div>
          <span
            className="font-weight-bold"
            style={{ color: "#1e293b", fontSize: "0.85rem" }}
          >
            {val.toFixed(1)}%
          </span>
        </div>
      );
    }
    if (
      key.includes("AMOUNT") ||
      key.includes("VALUE") ||
      key.includes("COMMISSION") ||
      key.includes("REVENUE") ||
      key.includes("PIPELINE") ||
      key.includes("PRICE")
    ) {
      return (
        <div className="d-flex align-items-center">
          <span
            className="mr-1 text-muted font-weight-bold"
            style={{ fontSize: "0.7rem" }}
          >
            $
          </span>
          <strong
            style={{
              color: "#0f172a",
              fontSize: "0.95rem",
              letterSpacing: "-0.2px"
            }}
          >
            {parseFloat(value).toLocaleString(undefined, {
              minimumFractionDigits: 2
            })}
          </strong>
        </div>
      );
    }
    if (key === "ENGAGEMENT_TIER") {
      const colors = {
        "Highly Engaged": "linear-gradient(135deg, #2ec4b6, #2196f3)",
        "Moderately Engaged": "linear-gradient(135deg, #3a86ff, #8338ec)",
        "Low Engagement": "linear-gradient(135deg, #f4a261, #e76f51)",
        "At Risk": "linear-gradient(135deg, #e85d4a, #d00000)"
      };
      return (
        <Badge
          style={{
            background: colors[value] || "#94a3b8",
            border: "none",
            borderRadius: "6px",
            padding: "6px 12px",
            fontWeight: "700",
            fontSize: "0.75rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
          }}
        >
          {value.toUpperCase()}
        </Badge>
      );
    }
    if (
      typeof value === "number" &&
      !key.includes("ID") &&
      !key.includes("SCORES")
    ) {
      return (
        <span
          className="font-weight-bold text-dark"
          style={{ fontSize: "0.9rem" }}
        >
          {value.toLocaleString()}
        </span>
      );
    }
    return (
      <span className="text-secondary" style={{ fontSize: "0.85rem" }}>
        {value}
      </span>
    );
  };

  const renderTable = tableData => {
    if (!tableData || tableData.length === 0) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center py-5">
          <div className="mb-4 text-muted opacity-20">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          </div>
          <p className="text-muted font-weight-medium">
            No analytical patterns found for this segment.
          </p>
        </div>
      );
    }

    const headers = Object.keys(tableData[0]);

    return (
      <div style={{ overflowX: "auto", borderRadius: "12px" }}>
        <Table hover borderless className="custom-table mb-0">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="text-uppercase py-3 px-4"
                  style={{
                    background: "#f8fafc",
                    color: "#64748b",
                    fontSize: "10px",
                    fontWeight: "800",
                    letterSpacing: "1px"
                  }}
                >
                  {h.split("_").join(" ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, i) => (
              <tr key={i} className="insight-row border-bottom">
                {headers.map((h, j) => (
                  <td key={j} className="align-middle py-3 px-4">
                    {formatValue(h, row[h])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const handleExport = () => {
    if (!data || data.length === 0) return;

    // Convert data to CSV
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","), // header row
      ...data.map(row =>
        headers
          .map(header => {
            let val =
              row[header] === null || row[header] === undefined
                ? ""
                : row[header];
            // Escape commas and quotes
            if (
              typeof val === "string" &&
              (val.includes(",") || val.includes('"') || val.includes("\n"))
            ) {
              val = `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          })
          .join(",")
      )
    ].join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${section.id}_export_${moment().format("YYYY-MM-DD")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      id={section.id}
      ref={sectionRef}
      className="insight-section scroll-mt-20 mb-5"
    >
      <Card
        className={`glass-card border-0 overflow-hidden transition-all ${
          isActive ? "active-section-card shadow-blue-vivid" : "shadow-lg"
        }`}
        style={{
          borderRadius: "24px",
          background: isActive
            ? "rgba(255, 255, 255, 1)"
            : "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          border: isActive ? "2px solid #3a86ff" : "1px solid rgba(0,0,0,0.05)",
          transform: isActive ? "scale(1.01)" : "scale(1)",
          zIndex: isActive ? 2 : 1,
          position: "relative",
          boxShadow: isActive
            ? "0 20px 40px rgba(58, 134, 255, 0.25)"
            : "0 10px 15px -3px rgba(0,0,0,0.1)"
        }}
      >
        <CardBody className="p-0">
          <div
            className="p-4 d-flex justify-content-between align-items-center border-bottom"
            style={{
              background: isActive
                ? "linear-gradient(rgba(58, 134, 255, 0.05), rgba(58, 134, 255, 0.01))"
                : "linear-gradient(rgba(248, 250, 252, 0.5), rgba(248, 250, 252, 0.2))"
            }}
          >
            <div className="d-flex align-items-center">
              <div
                className="section-icon-box mr-3"
                style={{
                  width: "44px",
                  height: "44px",
                  background: isActive ? "#3a86ff" : "white",
                  color: isActive ? "white" : "inherit",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyCenter: "center",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  fontSize: "1.4rem",
                  transition: "all 0.4s ease"
                }}
              >
                {section.icon}
              </div>
              <div>
                <h5
                  className={`font-weight-bold mb-0 ${
                    isActive ? "text-primary" : "text-dark"
                  }`}
                  style={{
                    letterSpacing: "-0.4px",
                    transition: "all 0.4s ease"
                  }}
                >
                  {section.label}
                </h5>
                <p className="text-muted small mb-0">{section.description}</p>
              </div>
            </div>
          </div>
          <div className="p-2">
            {error ? (
              <div className="text-center py-5">
                <div className="mb-2">⚠️</div>
                <div className="text-muted small">
                  No records Found for this analysis
                </div>
              </div>
            ) : !hasLoaded && !loading && index !== 0 ? (
              <div className="text-center py-5 opacity-50">
                <Spinner size="sm" className="mr-2" />
                <span className="small">Scroll to load analysis...</span>
              </div>
            ) : (loading && (data.length === 0 || index === 0)) ||
              (!hasLoaded && index === 0) ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3 text-muted small">
                  Performing Performance Analysis...
                </p>
              </div>
            ) : (
              <div className="animate-fadeIn">{renderTable(data)}</div>
            )}
          </div>
          <div className="p-3 border-top bg-light-soft text-right">
            <Button
              color="link"
              size="sm"
              className="text-muted font-weight-bold"
              style={{ textDecoration: "none" }}
              disabled={!data || data.length === 0}
              onClick={handleExport}
            >
              EXPORT SEGMENT{" "}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ml-1"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

const SnowFlakeInsights = ({ partnerId, startDate, endDate }) => {
  const [activeSection, setActiveSection] = useState(sectionsList[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // Highlight the section when it crosses the 50% vertical line of the viewport
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        // rootMargin captures the "active" zone.
        // Setting it to focus on a narrow band around the 50% mark (vh50)
        rootMargin: "-40% 0px -50% 0px",
        threshold: 0
      }
    );

    const sectionElements = document.querySelectorAll(".insight-section");
    sectionElements.forEach(el => observer.observe(el));

    return () => {
      sectionElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const scrollTo = id => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      // Minor delay to let the observer update naturally, but we force it for instant feedback
      setActiveSection(id);
    }
  };

  return (
    <div className="snowflake-insights-container">
      <Row>
        {/* Sticky Quick Nav Sidebar */}
        <Col lg={3} className="d-none d-lg-block">
          <div
            className="sticky-sidebar pt-2"
            style={{ position: "sticky", top: "0px" }}
          >
            <div className="nav-header mb-4 px-3">
              <h6 className="text-uppercase text-primary font-weight-bold small letter-spacing-1">
                Analysis Navigation
              </h6>
              <div
                className="bg-primary mt-1"
                style={{ height: "3px", width: "24px", borderRadius: "100px" }}
              ></div>
            </div>
            <div className="nav flex-column quick-nav">
              {sectionsList.map(s => (
                <div
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`nav-link d-flex align-items-center mb-1 py-2 px-3 border-radius-lg transition-all cursor-pointer ${
                    activeSection === s.id ? "active-link" : "text-muted"
                  }`}
                  style={{
                    cursor: "pointer",
                    borderRadius: "12px",
                    background:
                      activeSection === s.id
                        ? "rgba(58, 134, 255, 0.08)"
                        : "transparent",
                    color: activeSection === s.id ? "#3a86ff" : "#64748b",
                    fontWeight: activeSection === s.id ? "700" : "500",
                    fontSize: "0.85rem"
                  }}
                >
                  <span className="mr-3">{s.icon}</span>
                  <span>
                    {s.label.split(" ")[0]} {s.label.split(" ")[1] || ""}
                  </span>
                  {activeSection === s.id && (
                    <div
                      className="ml-auto bg-primary rounded-circle"
                      style={{ width: "6px", height: "6px" }}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Col>

        {/* Main Content Area */}
        <Col lg={9}>
          <div className="insights-header mb-5">
            <h2
              className="font-weight-bold text-dark mb-4"
              style={{ fontSize: "2rem", letterSpacing: "-0.8px" }}
            >
              Snowflake Logic & Attribution
            </h2>
            <p className="text-muted" style={{ fontSize: "1.1rem" }}>
              Multi-dimensional operational logic and behavioral attribution
              matrices, updated every 15 minutes.
            </p>
          </div>

          <div className="insights-vertical-stack">
            {sectionsList.map((section, index) => (
              <SnowFlakeInsightCard
                key={section.id}
                section={section}
                partnerId={partnerId}
                startDate={startDate}
                endDate={endDate}
                index={index}
                isActive={activeSection === section.id}
              />
            ))}
          </div>
        </Col>
      </Row>

      <style>{`
        .quick-nav .nav-link:hover {
            background: rgba(58, 134, 255, 0.04) !important;
            transform: translateX(4px);
        }
        .insight-row:hover {
            background: rgba(58, 134, 255, 0.02) !important;
        }
        .scroll-mt-20 {
            scroll-margin-top: 100px;
        }
        .bg-gradient-primary {
            background: linear-gradient(135deg, #3a86ff, #8338ec);
        }
        .transition-all {
            transition: all 0.3s ease;
        }
        .letter-spacing-1 {
            letter-spacing: 1px;
        }
        .x-small {
            font-size: 0.75rem;
        }
        .section-icon-box {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        /* Custom Thin Scrollbar */
        .insight-section ::-webkit-scrollbar {
            height: 4px;
            width: 4px;
        }
        .insight-section ::-webkit-scrollbar-track {
            background: #f1f5f9;
        }
        .insight-section ::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
        }
        .insight-section ::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default SnowFlakeInsights;
